import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Handshake, Building2, Globe, Shield, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const partnerTypes = [
  {
    icon: Building2,
    title: 'Systems Integrators & Consultancies',
    desc: 'Help your clients operationalize their revenue data. Forsyt turns CRM and call intelligence into a prescriptive action layer your clients can use from Day 1.',
    benefits: [
      'White-label deployment options for your client engagements',
      'Faster time-to-value than custom-built solutions',
      'Recurring revenue from ongoing Forsyt licenses',
      'Training and certification for your consulting team',
    ],
  },
  {
    icon: Globe,
    title: 'Technology Partners',
    desc: 'Integrate your product with Forsyt to deliver more value to shared customers. CRM, call intelligence, email, and messaging platforms welcome.',
    benefits: [
      'API-first architecture for deep integrations',
      'Joint go-to-market opportunities',
      'Shared customer success and support',
      'Co-marketing and co-selling programs',
    ],
  },
  {
    icon: Zap,
    title: 'Referral Partners',
    desc: 'Know a revenue team that needs Forsyt? Earn a referral fee for every qualified introduction that converts to a customer.',
    benefits: [
      'Simple referral tracking and payouts',
      'Dedicated partner manager',
      'Access to Forsyt demo environment for pitching',
      'Priority support for referred customers',
    ],
  },
];

export default function Partners() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="Partners, Forsyt Revenue OS"
        description="Partner with Forsyt to help revenue teams move from dashboards to action. SI, technology, and referral partner programs available."
        path="/resources/partners"
      />

      {/* Hero */}
      <section className="pt-20 pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Handshake className="w-10 h-10 text-blue-600 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="partners-headline">
            Partner with Forsyt
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Help revenue teams move from dashboards to action engines. We're building a partner ecosystem for consultancies, technology companies, and referral partners.
          </p>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 space-y-10">
          {partnerTypes.map((partner, i) => {
            const Icon = partner.icon;
            return (
              <div key={i} className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all" data-testid={`partner-type-${i}`}>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{partner.title}</h2>
                    <p className="text-gray-600 mt-1">{partner.desc}</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 ml-16">
                  {partner.benefits.map((benefit, j) => (
                    <div key={j} className="flex items-start gap-2">
                      <Shield className="w-4 h-4 text-emerald-500 shrink-0 mt-1" />
                      <p className="text-sm text-gray-700">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Interested in partnering?</h2>
          <p className="text-gray-600 mb-8">
            We're early-stage and selective about partners. If you work with revenue teams and see the gap between data and execution, let's talk.
          </p>
          <a
            href="https://calendar.app.google/nswB9pt6ALLZ8xoy7"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-3.5 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all"
          >
            Schedule a Partner Call <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
